/**
 * `useSavedSearches` — the saved-search query hook (frontend.md §5 step 9, §2.4).
 *
 * A saved search is a serialized search URL state (frontend.md §2.2, §3.2 — "a saved
 * search is just a serialized URL"). The backend `saved_searches` endpoint persists them
 * (frontend.md open-question §11 — backend is preferred for the v2 lift).
 *
 * NOTE: the backend v1 `GET /saved_searches` returns an empty list — a documented seam
 * (`rest_app.py`: no `SavedSearchRepo` in the v1 api-core port set; the integration layer
 * threads it in). And there is no `POST`/`DELETE` route wired yet — so the create/delete
 * mutations are intentionally NOT implemented here: there is nothing to call. The
 * `<SavedSearchesPanel>` renders the (currently empty) list and shows a "saved searches
 * land at integration" affordance rather than a broken save button. When the backend
 * adds the write routes, the mutations slot in here.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { fetchSavedSearches } from '@/api';
import type { SavedSearchList } from '@/types';
import { queryKeys } from '@/providers';
import { useErrorHandler } from './useErrorHandler';

export function useSavedSearches(): UseQueryResult<SavedSearchList, Error> {
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: queryKeys.savedSearches(),
    queryFn: async () => {
      try {
        return await fetchSavedSearches();
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}
